import React from 'react';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import { FormattedMessage, useIntl } from 'react-intl';
import { DropdownMenuHeader, DropdownMenuItem } from '@bibliocommons/base-dropdown';
import { eventShape } from 'app/shapes/events';
import { selectAppConfig } from 'app/selectors/AppSelector';
import { selectLocationEntities, selectPlaceEntities } from 'app/selectors/EntitiesSelector';
import { formatTitle, formatDescription, formatDate, formatAddress, isAllDayEvent } from 'app/helpers/events/utils';
import PropTypes from 'prop-types';

const GOOGLE_CALENDER = 'https://www.google.com/calendar/render?action=TEMPLATE&';
const OUTLOOK_CALENDAR = 'https://outlook.live.com/calendar/0/action/compose?';
const ONLINE_EVENT = 'Online event';

export default function AddToCalendar({ event, trackAddToCalendarClick }) {
  const intl = useIntl();

  const appConfig = useSelector(selectAppConfig);
  const locationEntities = useSelector(selectLocationEntities);
  const places = useSelector(selectPlaceEntities);

  const appVersion = appConfig?.get('coreVersion');
  const baseURL = appConfig.get('baseURL');
  const filename = `${appConfig.get('libraryDomain')}.ics`;

  const eventTitle = formatTitle(event.getIn(['definition', 'title']).trim());
  const eventId = event.get('id');
  const eventDescription = formatDescription(event.getIn(['definition', 'description']).trim(), 240);
  const eventStart = formatDate(event.get('indexStart'), 'YYYYMMDDTHHmmss');
  const eventEnd = formatDate(event.get('indexEnd'), 'YYYYMMDDTHHmmss');
  const isCancelled = event.getIn(['definition', 'isCancelled']);
  const eventLink = `${baseURL}/events/${eventId}`;

  const isVirtual = event.getIn(['definition', 'isVirtual']);
  const location = event.getIn(['definition', 'branchLocationId'])
    ? locationEntities.get(event.getIn(['definition', 'branchLocationId']))
    : places.get(event.getIn(['definition', 'nonBranchLocationId']));
  const eventLocation = isVirtual ? ONLINE_EVENT : formatAddress(location);

  const appleOutlookText = intl.formatMessage({ id: 'apple_and_outlook' });
  const googleCalendarText = intl.formatMessage({ id: 'google_calendar' });
  const outlookText = intl.formatMessage({ id: 'outlook' });

  function createCalendarFile() {
    trackAddToCalendarClick({
      linkText: appleOutlookText,
      eventId
    });

    const calendar = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      `PRODID:-//BiblioCommons//NONSGML BiblioEvents ${appVersion}//EN`,
      `${isCancelled ? 'METHOD:CANCELLED' : ''}`,
      'BEGIN:VEVENT',
      `UID:${eventId}@${baseURL}`,
      `SUMMARY:${eventTitle}`,
      `DTSTAMP:${eventStart}Z`,
      `DTSTART:${eventStart}Z`,
      `DTEND:${eventEnd}Z`,
      `${isCancelled ? 'STATUS:CANCELLED' : ''}`,
      `DESCRIPTION:${eventDescription}...\\n\\n View all details for this event:\\n ${eventLink}`,
      `LOCATION:${eventLocation}`,
      `URL:${eventLink}`,
      'CLASS:PUBLIC',
      'TRANSP:TRANSPARENT',
      'END:VEVENT',
      'BEGIN:VALARM',
      'TRIGGER:-PT1440M',
      'ACTION:DISPLAY',
      `DESCRIPTION:Event Reminder - ${eventTitle}`,
      'END:VALARM',
      'END:VCALENDAR'
    ]
      .filter(item => item)
      .join('\n');

    const file = new File([calendar], filename, { type: 'text/calendar' });
    const url = URL.createObjectURL(file);
    window.open(url);
  }

  function openGoogleCalendar() {
    trackAddToCalendarClick({
      linkText: googleCalendarText,
      eventId
    });

    const googleCalendarLink = `${GOOGLE_CALENDER}text=${eventTitle}&details=${eventDescription}&dates=${eventStart}Z/${eventEnd}Z&location=${eventLocation}`;
    window.open(encodeURI(googleCalendarLink));
  }

  function openOutlookCalendar() {
    trackAddToCalendarClick({
      linkText: outlookText,
      eventId
    });

    const outlookCalendarLink = `${OUTLOOK_CALENDAR}allday=${isAllDayEvent}&subject=${eventTitle}&body=${eventDescription}&startdt=${eventStart}Z&enddt=${eventEnd}Z&location=${eventLocation}`;
    window.open(encodeURI(outlookCalendarLink));
  }

  return (
    <>
      <DropdownMenuHeader>
        <FormattedMessage id="add_to_calendar" />
      </DropdownMenuHeader>
      <DropdownMenuItem
        item={Immutable.Map({ label: appleOutlookText })}
        dataKey="calender_download_link"
        handleClick={createCalendarFile}
      />
      <DropdownMenuItem
        item={Immutable.Map({ label: googleCalendarText })}
        dataKey="google_calendar_link"
        handleClick={openGoogleCalendar}
      />
      <DropdownMenuItem
        item={Immutable.Map({ label: outlookText })}
        dataKey="outlook_calendar_link"
        handleClick={openOutlookCalendar}
      />
    </>
  );
}

AddToCalendar.propTypes = {
  event: eventShape.isRequired,
  trackAddToCalendarClick: PropTypes.func.isRequired
};
